/**
 * Utilitaire de validation de mot de passe
 * Vérifie la conformité du mot de passe aux règles de sécurité
 */

/**
 * Interface pour les résultats de validation de mot de passe
 */
export interface PasswordValidationResult {
  isValid: boolean;
  hasMinLength: boolean;
  hasUpperCase: boolean;
  hasLowerCase: boolean;
  hasDigit: boolean;
  hasSpecialChar: boolean;
  meetsComplexityRequirements: boolean;
}

/**
 * Valide un mot de passe selon les critères de sécurité définis
 * @param password Le mot de passe à valider
 * @returns Un objet contenant les résultats de validation
 */
export const validatePassword = (password: string): PasswordValidationResult => {
  // Vérification de la longueur minimale (8 caractères)
  const hasMinLength = password.length >= 8;
  
  // Vérification de la présence d'au moins une lettre majuscule
  const hasUpperCase = /[A-Z]/.test(password);
  
  // Vérification de la présence d'au moins une lettre minuscule
  const hasLowerCase = /[a-z]/.test(password);
  
  // Vérification de la présence d'au moins un chiffre
  const hasDigit = /[0-9]/.test(password);
  
  // Vérification de la présence d'au moins un caractère spécial
  const hasSpecialChar = /[!@#$%^&*()_\-+={}[\]|\\:;"'<>,.?/]/.test(password);
  
  // Compte le nombre de critères de complexité satisfaits
  const complexityCriteriaMet = [
    hasUpperCase,
    hasLowerCase,
    hasDigit,
    hasSpecialChar
  ].filter(Boolean).length;
  
  // Vérifie si au moins 3 des 4 critères de complexité sont satisfaits
  const meetsComplexityRequirements = complexityCriteriaMet >= 3;
  
  // Le mot de passe est valide s'il a la longueur minimale et satisfait les exigences de complexité
  const isValid = hasMinLength && meetsComplexityRequirements;
  
  return {
    isValid,
    hasMinLength,
    hasUpperCase,
    hasLowerCase,
    hasDigit,
    hasSpecialChar,
    meetsComplexityRequirements
  };
};
